/**
 * Cliente de la API de administración: tipos del contrato (openapi-fetch) + validación Zod de cada
 * respuesta. No sabe de sesiones: recibe una función que entrega el token vigente.
 */
import createClient, { type Middleware } from "openapi-fetch";
import type { z } from "zod";
import type { components, paths } from "./esquema";
import {
  esquemaError,
  esquemaFicha,
  esquemaHecho,
  esquemaInstituciones,
  esquemaPagina,
  esquemaUsuario,
  esquemaUsuarios,
} from "./esquemas";

export type Esquemas = components["schemas"];
export type Usuario = Esquemas["Usuario"];
export type Acceso = Esquemas["Acceso"];
export type UsuarioConAccesos = Esquemas["UsuarioConAccesos"];
export type Ficha = Esquemas["Ficha"];
export type Institucion = Esquemas["Institucion"];
export type RegistroAuditoria = Esquemas["RegistroAuditoria"];
export type PaginaAuditoria = Esquemas["PaginaAuditoria"];
export type Perfil = Esquemas["Perfil"];
export type EstadoUsuario = Esquemas["EstadoUsuario"];
export type Relacion = Esquemas["Relacion"];
export type Invitacion = Esquemas["Invitacion"];
export type Concesion = Esquemas["Concesion"];

/** Acciones sobre la cuenta que no llevan más datos que, a veces, un motivo. */
export type AccionCuenta = "reenviar-invitacion" | "suspender" | "reactivar" | "baja" | "cerrar-sesiones";

/** Error de la API con su código de estado. Un 409 trae en el mensaje la regla que no se cumple. */
export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = "ErrorApi";
  }
}

export interface OpcionesCliente {
  readonly baseUrl: string;
  /** Token de identidad vigente, o null si no hay sesión. */
  readonly obtenerToken: () => Promise<string | null>;
  /** Se llama ante un 401: la sesión ya no sirve. */
  readonly alPerderSesion?: () => void;
  readonly fetch?: typeof globalThis.fetch;
}

function validar(esquema: z.ZodType, datos: unknown, ruta: string): unknown {
  const r = esquema.safeParse(datos);
  if (!r.success) throw new ErrorApi(0, "respuesta_invalida", `La respuesta de ${ruta} no cumple el contrato`);
  return r.data;
}

export function crearClienteApi(opciones: OpcionesCliente) {
  const cliente = createClient<paths>({
    baseUrl: opciones.baseUrl,
    ...(opciones.fetch ? { fetch: opciones.fetch } : {}),
  });
  const autenticacion: Middleware = {
    async onRequest({ request }) {
      const token = await opciones.obtenerToken();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) opciones.alPerderSesion?.();
      return response;
    },
  };
  cliente.use(autenticacion);

  async function resultado<T>(
    peticion: Promise<{ data?: unknown; error?: unknown; response: Response }>,
    esquema: z.ZodType,
    ruta: string,
  ): Promise<T> {
    const { data, error, response } = await peticion;
    if (!response.ok) {
      const e = esquemaError.safeParse(error);
      throw new ErrorApi(
        response.status,
        e.success ? e.data.codigo : "error",
        e.success ? e.data.mensaje : response.statusText,
      );
    }
    // Validada contra el esquema Zod, que las pruebas de tipos atan al contrato.
    return validar(esquema, data, ruta) as T;
  }

  const hecho = (p: Promise<{ data?: unknown; error?: unknown; response: Response }>, ruta: string) =>
    resultado<{ ok: true }>(p, esquemaHecho, ruta).then(() => undefined);
  const ruta = (usuarioId: string) => ({ params: { path: { usuarioId } } });

  return {
    usuarios: () =>
      resultado<{ usuarios: UsuarioConAccesos[] }>(
        cliente.GET("/admin/usuarios"),
        esquemaUsuarios,
        "/admin/usuarios",
      ).then((r) => r.usuarios),
    ficha: (usuarioId: string) =>
      resultado<Ficha>(cliente.GET("/admin/usuarios/{usuarioId}", ruta(usuarioId)), esquemaFicha, "ficha"),
    historial: (usuarioId: string, cursor: string | null) =>
      resultado<PaginaAuditoria>(
        cliente.GET("/admin/usuarios/{usuarioId}/historial", {
          params: { path: { usuarioId }, query: cursor ? { cursor } : {} },
        }),
        esquemaPagina,
        "historial",
      ),
    invitar: (invitacion: Invitacion) =>
      resultado<Usuario>(cliente.POST("/admin/usuarios", { body: invitacion }), esquemaUsuario, "invitar"),
    accionCuenta: (usuarioId: string, accion: AccionCuenta, motivo: string | null) => {
      const r = ruta(usuarioId);
      const conMotivo = { ...r, body: { motivo } };
      switch (accion) {
        case "reenviar-invitacion":
          return hecho(cliente.POST("/admin/usuarios/{usuarioId}/reenviar-invitacion", r), accion);
        case "suspender":
          return hecho(cliente.POST("/admin/usuarios/{usuarioId}/suspender", conMotivo), accion);
        case "reactivar":
          return hecho(cliente.POST("/admin/usuarios/{usuarioId}/reactivar", r), accion);
        case "baja":
          return hecho(cliente.POST("/admin/usuarios/{usuarioId}/baja", conMotivo), accion);
        case "cerrar-sesiones":
          return hecho(cliente.POST("/admin/usuarios/{usuarioId}/cerrar-sesiones", r), accion);
      }
    },
    conceder: (usuarioId: string, institucion: string, concesion: Concesion) =>
      hecho(
        cliente.PUT("/admin/usuarios/{usuarioId}/accesos/{institucion}", {
          params: { path: { usuarioId, institucion } },
          body: concesion,
        }),
        "conceder",
      ),
    cambiarPerfil: (usuarioId: string, institucion: string, perfil: Perfil) =>
      hecho(
        cliente.PATCH("/admin/usuarios/{usuarioId}/accesos/{institucion}", {
          params: { path: { usuarioId, institucion } },
          body: { perfil },
        }),
        "cambiarPerfil",
      ),
    quitar: (usuarioId: string, institucion: string) =>
      hecho(
        cliente.DELETE("/admin/usuarios/{usuarioId}/accesos/{institucion}", {
          params: { path: { usuarioId, institucion } },
        }),
        "quitar",
      ),
    instituciones: () =>
      resultado<{ instituciones: Institucion[] }>(
        cliente.GET("/admin/instituciones"),
        esquemaInstituciones,
        "/admin/instituciones",
      ).then((r) => r.instituciones),
    guardarInstitucion: (codigo: string, nombre: string, habilitada: boolean) =>
      hecho(
        cliente.PUT("/admin/instituciones/{institucion}", {
          params: { path: { institucion: codigo } },
          body: { nombre, habilitada },
        }),
        "guardarInstitucion",
      ),
    auditoria: (dia: string, cursor: string | null) =>
      resultado<PaginaAuditoria>(
        cliente.GET("/admin/auditoria", { params: { query: cursor ? { dia, cursor } : { dia } } }),
        esquemaPagina,
        "/admin/auditoria",
      ),
  };
}

export type ClienteApi = ReturnType<typeof crearClienteApi>;
